import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PlusIcon } from '@heroicons/react/24/outline';
import { confirmDialog } from '../components/ConfirmDialog';

type FleetStats = {
  units: number;
  unitsActive: number;
  unitsOutOfService: number;
  pendingAlerts: number;
  overdueAlerts: number;
  openAppointments: number;
};

type Fleet = {
  id: string;
  clientId?: string | null;
  name: string;
  cuit?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  status?: string | null;
  priority?: string | null;
  notes?: string | null;
  stats: FleetStats;
};

type ClientOption = {
  id: string;
  companyName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

type FleetForm = {
  clientId: string;
  name: string;
  cuit: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  status: string;
  priority: string;
  notes: string;
};

const initialForm: FleetForm = {
  clientId: '',
  name: '',
  cuit: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  status: 'activa',
  priority: 'media',
  notes: '',
};

const Fleets: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('todas');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState<Fleet | null>(null);
  const [form, setForm] = useState<FleetForm>(initialForm);

  const loadClients = useCallback(async () => {
    try {
      const response = await axios.get('/api/clients');
      const rows: ClientOption[] = Array.isArray(response.data) ? response.data : [];
      setClients(rows.filter((c) => Boolean(c.companyName || c.firstName || c.lastName)));
    } catch {
      setClients([]);
    }
  }, []);

  const loadFleets = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get('/api/fleets', { params: { search, status } });
      setFleets((response.data?.fleets || []) as Fleet[]);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudieron cargar las flotas');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadFleets();
  }, [loadFleets]);

  const totals = useMemo(() => {
    return fleets.reduce(
      (acc, f) => {
        acc.fleets += 1;
        acc.units += Number(f.stats?.units || 0);
        acc.alerts += Number(f.stats?.pendingAlerts || 0);
        acc.overdue += Number(f.stats?.overdueAlerts || 0);
        return acc;
      },
      { fleets: 0, units: 0, alerts: 0, overdue: 0 }
    );
  }, [fleets]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setShowDialog(true);
  };

  const openEdit = (fleet: Fleet) => {
    setEditing(fleet);
    setForm({
      clientId: fleet.clientId || '',
      name: fleet.name || '',
      cuit: fleet.cuit || '',
      contactName: fleet.contactName || '',
      contactPhone: fleet.contactPhone || '',
      contactEmail: fleet.contactEmail || '',
      status: fleet.status || 'activa',
      priority: fleet.priority || 'media',
      notes: fleet.notes || '',
    });
    setShowDialog(true);
  };

  const saveFleet = async () => {
    if (!form.name.trim()) {
      toast.error('Ingresá el nombre de la flota');
      return;
    }
    const payload = {
      clientId: form.clientId || null,
      name: form.name.trim(),
      cuit: form.cuit || null,
      contactName: form.contactName || null,
      contactPhone: form.contactPhone || null,
      contactEmail: form.contactEmail || null,
      status: form.status || 'activa',
      priority: form.priority || 'media',
      notes: form.notes || null,
    };
    try {
      if (editing) await axios.put(`/api/fleets/${editing.id}`, payload);
      else await axios.post('/api/fleets', payload);
      toast.success(editing ? 'Flota actualizada' : 'Flota creada');
      setShowDialog(false);
      await loadFleets({ silent: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo guardar la flota');
    }
  };

  const removeFleet = async (fleet: Fleet) => {
    if (!await confirmDialog(`Eliminar flota ${fleet.name}?`)) return;
    try {
      await axios.delete(`/api/fleets/${fleet.id}`);
      toast.success('Flota eliminada');
      await loadFleets({ silent: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo eliminar la flota');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Flotas</h1>
          <p className="text-sm text-muted-foreground">Seguimiento de empresas y unidades</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/fleets/calendar')}>Calendario</Button>
          <Button variant="outline" onClick={() => navigate('/fleets/reports')}>Reportes</Button>
          <Button onClick={openCreate} className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90">
            <PlusIcon className="h-4 w-4 mr-2" />
            Nueva Flota
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Flotas</div><div className="text-2xl font-bold">{totals.fleets}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Unidades</div><div className="text-2xl font-bold">{totals.units}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Alertas pendientes</div><div className="text-2xl font-bold">{totals.alerts}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Vencidas</div><div className="text-2xl font-bold text-red-600">{totals.overdue}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Listado</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <Input placeholder="Buscar por nombre, CUIT o contacto..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <select className="h-10 rounded-md border border-input bg-background px-3" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="todas">Todas</option>
              <option value="activa">Activa</option>
              <option value="pausada">Pausada</option>
              <option value="inactiva">Inactiva</option>
            </select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Flota</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Unidades</TableHead>
                  <TableHead>Alertas</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={6}>Cargando...</TableCell></TableRow>
                )}
                {!loading && fleets.length === 0 && (
                  <TableRow><TableCell colSpan={6}>No hay flotas</TableCell></TableRow>
                )}
                {!loading && fleets.map((fleet) => (
                  <TableRow key={fleet.id}>
                    <TableCell>
                      <div className="font-medium">{fleet.name}</div>
                      <div className="text-xs text-muted-foreground">{fleet.cuit || '-'}</div>
                    </TableCell>
                    <TableCell>
                      <div>{fleet.contactName || '-'}</div>
                      <div className="text-xs text-muted-foreground">{fleet.contactPhone || fleet.contactEmail || '-'}</div>
                    </TableCell>
                    <TableCell>{fleet.stats?.units || 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{fleet.stats?.pendingAlerts || 0}</Badge>
                        {Number(fleet.stats?.overdueAlerts || 0) > 0 && <Badge variant="destructive">{fleet.stats.overdueAlerts}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{fleet.status || 'activa'}</Badge></TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/fleets/${fleet.id}`)}>Ver</Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(fleet)}>Editar</Button>
                      <Button variant="destructive" size="sm" onClick={() => removeFleet(fleet)}>Eliminar</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Editar flota' : 'Nueva flota'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Empresa / Cliente (opcional)</Label>
              <select className="h-10 w-full rounded-md border border-input bg-background px-3" value={form.clientId} onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value }))}>
                <option value="">Sin vincular</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.companyName || `${c.firstName || ''} ${c.lastName || ''}`.trim()}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Nombre de flota</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="space-y-2"><Label>CUIT</Label><Input value={form.cuit} onChange={(e) => setForm((p) => ({ ...p, cuit: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Contacto</Label><Input value={form.contactName} onChange={(e) => setForm((p) => ({ ...p, contactName: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Teléfono</Label><Input value={form.contactPhone} onChange={(e) => setForm((p) => ({ ...p, contactPhone: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={form.contactEmail} onChange={(e) => setForm((p) => ({ ...p, contactEmail: e.target.value }))} /></div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <select className="h-10 w-full rounded-md border border-input bg-background px-3" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                <option value="activa">Activa</option><option value="pausada">Pausada</option><option value="inactiva">Inactiva</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <select className="h-10 w-full rounded-md border border-input bg-background px-3" value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}>
                <option value="baja">Baja</option><option value="media">Media</option><option value="alta">Alta</option>
              </select>
            </div>
            <div className="space-y-2 md:col-span-2"><Label>Notas</Label><Input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={saveFleet} className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-gray-100">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Fleets;
